/**
 * CirclesList — Circles discovery and "My Circles" page.
 * Route: /circles
 *
 * Auth model:
 *  - Public browse — anonymous users see the page with a "Log in to join" CTA.
 *  - My Circles tab and Join actions require authentication.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Users, AlertCircle, Hexagon } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import GlassPanel from '../components/GlassPanel';
import MonoAvatar from '../components/MonoAvatar';
import { getCircles, getMyCircles, getPublicCircles } from '../services/circleService';
import type { PublicCircle } from '../services/circleService';
import type { Circle } from '../types/circles';
import { slugify } from '../utils/circleSlug';
import '../styles/modern.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

// ─── Circle card (new design) ───────────────────────────────────────────────

function CircleCard({
  circle,
  joined,
  onClick,
  onJoin,
}: {
  circle: Circle | PublicCircle;
  joined: boolean;
  onClick: () => void;
  onJoin?: () => void;
}) {
  const hue = hashHue(circle.name);
  const memberCount = circle.memberCount ?? 0;
  const memberLimit = 'memberLimit' in circle ? circle.memberLimit : 50;
  const tags = 'categoryTags' in circle && circle.categoryTags ? circle.categoryTags : [];
  const desc = circle.description || '';

  return (
    <GlassPanel padding={0} onClick={onClick}>
      {/* gradient stripe header */}
      <div
        style={{
          height: 6,
          background: `linear-gradient(90deg, oklch(0.55 0.22 ${hue}), oklch(0.65 0.25 ${(hue + 60) % 360}))`,
          borderRadius: '16px 16px 0 0',
        }}
      />
      <div style={{ padding: 18 }}>
        {/* title + joined badge */}
        <div className="circles-card-title-row">
          <div className="circles-card-name">{circle.name}</div>
          {joined && <span className="circles-joined-badge">Joined</span>}
        </div>

        {/* description */}
        <div className="circles-card-desc">
          {desc.length > 120 ? desc.slice(0, 120) + '...' : desc}
        </div>

        {/* meta row */}
        <div className="circles-card-meta-row">
          <div className="circles-card-meta-left">
            <div className="circles-card-member-count">
              <Users size={13} />
              <span>{memberCount}/{memberLimit}</span>
            </div>
            {tags.slice(0, 2).map((t) => (
              <span key={t} className="tag-pill tag-pill--sm tag-pill--violet">{t}</span>
            ))}
          </div>
          <button
            className={`circles-card-action-btn ${joined ? 'circles-card-action-btn--open' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (joined) {
                onClick();
              } else if (onJoin) {
                onJoin();
              }
            }}
          >
            {joined ? 'Open' : 'Join'}
          </button>
        </div>

        {/* member count indicator */}
        <div className="circles-card-members-preview">
          <span style={{ fontSize: 11.5, color: 'rgba(167,139,250,0.55)' }}>
            {memberCount} {memberCount === 1 ? 'member' : 'members'}
          </span>
        </div>
      </div>
    </GlassPanel>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

type Tab = 'discover' | 'my-circles';

export default function CirclesList() {
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>('discover');
  const [circles, setCircles] = useState<Circle[]>([]);
  const [myCircleIds, setMyCircleIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastCircleId, setLastCircleId] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);

  // Public circles for anonymous users
  const [publicCircles, setPublicCircles] = useState<PublicCircle[]>([]);
  const [publicLoading, setPublicLoading] = useState(false);
  const [publicError, setPublicError] = useState<string | null>(null);
  const [publicHasMore, setPublicHasMore] = useState(false);
  const [publicLastId, setPublicLastId] = useState<string | undefined>();
  const [publicLoadingMore, setPublicLoadingMore] = useState(false);

  // ─── Fetch my circle IDs for joined badges ────────────────────────────
  const fetchMyCircleIds = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getMyCircles();
      const ids = new Set((data.circles || []).map((c: Circle) => c.id));
      setMyCircleIds(ids);
    } catch {
      // non-critical
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchMyCircleIds();
  }, [fetchMyCircleIds]);

  // ─── Fetch circles (authenticated) ─────────────────────────────────────
  const fetchCircles = useCallback(
    async (tab: Tab, append = false) => {
      if (!isAuthenticated) return;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      try {
        let data;
        if (tab === 'my-circles') {
          data = await getMyCircles();
        } else {
          data = await getCircles(
            { visibility: 'public', status: 'active' },
            { limit: 20, startAfter: append ? lastCircleId : undefined }
          );
        }

        const newCircles = data.circles || [];

        if (append) {
          setCircles((prev) => [...prev, ...newCircles]);
        } else {
          setCircles(newCircles);
        }

        setHasMore(data.hasMore ?? false);
        if (newCircles.length > 0) {
          setLastCircleId(newCircles[newCircles.length - 1].id);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load circles');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [isAuthenticated, lastCircleId]
  );

  // ─── Fetch public circles (anonymous) ──────────────────────────────────
  const fetchPublicCircles = useCallback(
    async (append = false) => {
      if (append) {
        setPublicLoadingMore(true);
      } else {
        setPublicLoading(true);
        setPublicError(null);
      }

      try {
        const data = await getPublicCircles({
          limit: 20,
          startAfter: append ? publicLastId : undefined,
        });

        const newCircles = data.circles || [];

        if (append) {
          setPublicCircles((prev) => [...prev, ...newCircles]);
        } else {
          setPublicCircles(newCircles);
        }

        setPublicHasMore(data.hasMore ?? false);
        if (newCircles.length > 0) {
          setPublicLastId(newCircles[newCircles.length - 1].id);
        }
      } catch (err: any) {
        setPublicError(err?.message || 'Failed to load circles');
      } finally {
        setPublicLoading(false);
        setPublicLoadingMore(false);
      }
    },
    [publicLastId]
  );

  // Reload on tab change (authenticated)
  useEffect(() => {
    if (isAuthenticated) {
      setCircles([]);
      setLastCircleId(undefined);
      setHasMore(false);
      fetchCircles(activeTab);
    }
  }, [activeTab, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load public circles for anonymous users
  useEffect(() => {
    if (!isAuthenticated) {
      fetchPublicCircles();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = useCallback(() => {
    if (isAuthenticated) {
      fetchCircles(activeTab, true);
    } else {
      fetchPublicCircles(true);
    }
  }, [activeTab, isAuthenticated, fetchCircles, fetchPublicCircles]);

  // ─── Structured data for SEO ──────────────────────────────────────────
  const circlesForSchema = isAuthenticated ? circles : publicCircles;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Circles — Community Groups | pplkpr',
    description: 'Join interest-based Circles to chat, plan events, and connect with people who share your passions.',
    url: 'https://pplkpr.ai/circles',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: circlesForSchema.slice(0, 10).map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Organization',
          name: c.name,
          description: c.description,
          memberOf: {
            '@type': 'QuantitativeValue',
            value: c.memberCount,
          },
        },
      })),
    },
  };

  // Determine displayed list
  const displayCircles = isAuthenticated ? circles : publicCircles;
  const displayLoading = isAuthenticated ? loading : publicLoading;
  const displayError = isAuthenticated ? error : publicError;
  const displayHasMore = isAuthenticated ? hasMore : publicHasMore;
  const displayLoadingMore = isAuthenticated ? loadingMore : publicLoadingMore;

  const discoverCount = isAuthenticated ? circles.length : publicCircles.length;
  const myCount = myCircleIds.size;

  return (
    <>

      <div className="page-shell">
        <div className="page-shell__inner" style={{ maxWidth: 920 }}>
          <SEO
            title="Circles — Community Groups | pplkpr"
            description="Join interest-based Circles to chat, plan events, and connect with people who share your passions."
            path="/circles"
          />
          <StructuredData data={structuredData} />

          {/* Page header */}
          <div className="page-header">
            <div>
              <h1 className="page-header__title">circles</h1>
              <div className="page-header__subtitle">
                interest-based community groups. up to 50 members each.
              </div>
            </div>
            {isAuthenticated && (
              <button
                className="primary-button"
                onClick={() => navigate('/circles/create')}
              >
                <Plus size={15} />
                create circle
              </button>
            )}
            {!isAuthenticated && (
              <button
                className="primary-button"
                onClick={redirectToLogin}
              >
                log in to join
              </button>
            )}
          </div>

          {/* Pill tabs */}
          {isAuthenticated && (
            <div style={{ marginBottom: 18 }}>
              <div className="pill-tabs">
                <button
                  className={`pill-tab ${activeTab === 'discover' ? 'pill-tab--active' : ''}`}
                  onClick={() => setActiveTab('discover')}
                >
                  Discover
                  <span className="pill-tab__count">{discoverCount}</span>
                </button>
                <button
                  className={`pill-tab ${activeTab === 'my-circles' ? 'pill-tab--active' : ''}`}
                  onClick={() => setActiveTab('my-circles')}
                >
                  My circles
                  <span className="pill-tab__count">{myCount}</span>
                </button>
              </div>
            </div>
          )}

          {/* Loading skeleton */}
          {displayLoading && (
            <div className="circles-redesign-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <GlassPanel key={i} padding={0}>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: '16px 16px 0 0' }} />
                  <div style={{ padding: 18 }}>
                    <div className="skel-line wide pulse" style={{ height: 18, marginBottom: 10 }} />
                    <div className="skel-line wide pulse" style={{ height: 12, marginBottom: 4 }} />
                    <div className="skel-line medium pulse" style={{ height: 12, marginBottom: 14 }} />
                    <div className="skel-line narrow pulse" style={{ height: 10 }} />
                  </div>
                </GlassPanel>
              ))}
            </div>
          )}

          {/* Error */}
          {displayError && !displayLoading && (
            <div className="circles-error">
              <AlertCircle size={20} />
              <p>{displayError}</p>
              <button
                className="primary-button"
                onClick={() => isAuthenticated ? fetchCircles(activeTab) : fetchPublicCircles()}
              >
                try again
              </button>
            </div>
          )}

          {/* Results */}
          {!displayLoading && !displayError && (
            <>
              {displayCircles.length === 0 ? (
                <div className="circles-empty">
                  <Hexagon size={32} />
                  <h3>
                    {activeTab === 'my-circles'
                      ? "you haven't joined any circles yet"
                      : 'no circles found'}
                  </h3>
                  <p>
                    {activeTab === 'my-circles'
                      ? 'explore the discover tab to find communities that match your interests.'
                      : 'be the first to create a circle!'}
                  </p>
                  {activeTab === 'my-circles' && (
                    <button className="primary-button" onClick={() => setActiveTab('discover')}>
                      discover circles
                    </button>
                  )}
                </div>
              ) : (
                <div className="circles-redesign-grid">
                  {displayCircles.map((c) => (
                    <CircleCard
                      key={c.id}
                      circle={c}
                      joined={myCircleIds.has(c.id)}
                      onClick={() => isAuthenticated ? navigate(`/circles/${slugify(c.name)}`, { state: { circleId: c.id } }) : redirectToLogin()}
                      onJoin={isAuthenticated ? undefined : redirectToLogin}
                    />
                  ))}
                </div>
              )}

              {displayHasMore && (
                <div style={{ marginTop: 28, textAlign: 'center' }}>
                  <button
                    className="circles-load-more-btn"
                    onClick={handleLoadMore}
                    disabled={displayLoadingMore}
                  >
                    {displayLoadingMore ? 'loading...' : 'load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
